/**
 * <s-fireflies> — a field of glowing lightning bugs that drift on their own,
 * gather gently toward the pointer, and scatter when the visitor clicks or taps.
 * Reduced-motion visitors get a still, softly glowing field instead.
 */
class SFireflies extends HTMLElement {
  #flies = [];
  #frame = 0;
  #pointer = { x: -9999, y: -9999, active: false };
  #reduced = false;
  #onMove = (event) => {
    this.#pointer.x = event.clientX;
    this.#pointer.y = event.clientY;
    this.#pointer.active = true;
  };
  #onLeave = () => {
    this.#pointer.active = false;
  };
  #onDown = (event) => {
    this.#scatter(event.clientX, event.clientY);
  };
  #tick = (time) => {
    this.#step(time);
    this.#frame = requestAnimationFrame(this.#tick);
  };

  connectedCallback() {
    const count = Math.min(Math.max(parseInt(this.dataset.count || '36', 10), 0), 120);
    const isSmall = window.matchMedia('(max-width: 767px)').matches;
    const total = isSmall ? Math.round(count * 0.6) : count;
    this.#reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const width = window.innerWidth;
    const height = window.innerHeight;
    for (let i = 0; i < total; i += 1) {
      const el = document.createElement('span');
      el.className = 'firefly';
      el.setAttribute('aria-hidden', 'true');
      // Depth: 0 = far (small, soft, dim), 1 = near (larger, crisp, bright).
      const depth = Math.random() ** 1.6;
      const size = 2 + depth * 3.5;
      el.style.setProperty('--firefly-size', `${size}px`);
      el.style.setProperty('--firefly-blur', `${(1 - depth) * 1.6}px`);
      this.append(el);
      const period = 3200 + Math.random() * 5200;
      this.#flies.push({
        el,
        depth,
        x: Math.random() * width,
        // Fireflies hang low over the grass, so most sit in the lower two thirds.
        y: height * (0.25 + Math.random() ** 0.7 * 0.75),
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        period,
        offset: Math.random() * period,
        double: Math.random() < 0.25,
        peak: 0.55 + depth * 0.45,
        wander: Math.random() * Math.PI * 2,
      });
    }

    if (this.#reduced) {
      for (const fly of this.#flies) {
        fly.el.style.transform = `translate3d(${fly.x}px, ${fly.y}px, 0)`;
        fly.el.style.opacity = String(0.35 + Math.random() * 0.5);
      }
      return;
    }

    window.addEventListener('pointermove', this.#onMove, { passive: true });
    window.addEventListener('pointerdown', this.#onDown, { passive: true });
    document.documentElement.addEventListener('pointerleave', this.#onLeave);
    this.#frame = requestAnimationFrame(this.#tick);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    window.removeEventListener('pointermove', this.#onMove);
    window.removeEventListener('pointerdown', this.#onDown);
    document.documentElement.removeEventListener('pointerleave', this.#onLeave);
    this.#flies = [];
    this.replaceChildren();
  }

  #scatter(x, y) {
    for (const fly of this.#flies) {
      const dx = fly.x - x;
      const dy = fly.y - y;
      const dist = Math.hypot(dx, dy) || 1;
      if (dist < 260) {
        const force = (260 - dist) / 260;
        fly.vx += (dx / dist) * force * 4;
        fly.vy += (dy / dist) * force * 4;
        fly.startle = true;
      }
    }
  }

  /** Brightness 0..1 for a firefly at a given time: a quick flash, a slower fade, then darkness. */
  #flash(fly, time) {
    const t = (time + fly.offset) % fly.period;
    const pulse = (start) => {
      const d = t - start;
      if (d < 0) return 0;
      if (d < 120) return d / 120;
      if (d < 260) return 1;
      if (d < 900) return 1 - ((d - 260) / 640) ** 0.6;
      return 0;
    };
    return Math.max(pulse(0), fly.double ? pulse(520) * 0.85 : 0);
  }

  #step(time) {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const { x: px, y: py, active } = this.#pointer;

    for (const fly of this.#flies) {
      if (fly.startle) {
        // A startled firefly flashes right away.
        fly.offset = (fly.period - (time % fly.period)) % fly.period;
        fly.startle = false;
      }
      const glow = this.#flash(fly, time);
      const speed = 0.4 + fly.depth * 0.6;

      fly.wander += (Math.random() - 0.5) * 0.08;
      fly.vx += Math.cos(fly.wander) * 0.012 * speed;
      // Lazy bobbing, plus the little upward swoop real fireflies make while flashing.
      fly.vy += Math.sin(fly.wander) * 0.008 * speed - glow * 0.03 * speed;

      if (active) {
        const dx = px - fly.x;
        const dy = py - fly.y;
        const dist = Math.hypot(dx, dy) || 1;
        if (dist < 260 && dist > 60) {
          fly.vx += (dx / dist) * 0.008;
          fly.vy += (dy / dist) * 0.008;
        } else if (dist <= 60) {
          fly.vx -= (dx / dist) * 0.04;
          fly.vy -= (dy / dist) * 0.04;
        }
      }

      // Drift back toward the lower part of the screen if a firefly climbs too high.
      if (fly.y < height * 0.2) fly.vy += 0.01;

      fly.vx *= 0.97;
      fly.vy *= 0.97;
      fly.x += fly.vx;
      fly.y += fly.vy;

      if (fly.x < -20) fly.x = width + 20;
      if (fly.x > width + 20) fly.x = -20;
      if (fly.y < -20) fly.y = height + 20;
      if (fly.y > height + 20) fly.y = height * 0.5;

      fly.el.style.opacity = String(glow * fly.peak);
      fly.el.style.transform = `translate3d(${fly.x}px, ${fly.y}px, 0) scale(${0.6 + glow * 0.6})`;
    }
  }
}

if (!customElements.get('s-fireflies')) {
  customElements.define('s-fireflies', SFireflies);
}
